import { apiClient } from './api';

export type CompetitionEntryType = 'INDIVIDUAL' | 'FIXED_PAIR';

export interface CompetitionConsent {
  rulesAccepted: boolean;
  privacyAccepted: boolean;
  resultDisplayAccepted: boolean;
  communicationAccepted: boolean;
}

export interface PublicCompetition {
  leagueType?: string | null;
  competitionPublicId: string;
  slug: string;
  publicationVersion: number;
  name: string;
  sportCode: string;
  timezone: string;
  status: string;
  registrationState: string;
  registrationOpensAt?: string | null;
  registrationClosesAt?: string | null;
  competitionStartsAt?: string | null;
  competitionEndsAt?: string | null;
  rulesVersion?: number | null;
  rulesUrl?: string | null;
  prizeDescription?: string | null;
  publishedAt?: string | null;
  consentNotices?: {
    privacyVersion?: string | null;
    resultDisplayVersion?: string | null;
    communicationVersion?: string | null;
  } | null;
}

export interface PublicCompetitionDivision {
  divisionPublicId: string;
  name: string;
  formatCode: string;
  levelLabel?: string | null;
  capacity?: number | null;
  registrationState: string;
  registrationClosesAt?: string | null;
  matchWindowStartsAt?: string | null;
  matchDeadlineAt?: string | null;
  scoringSummary?: string | null;
  tieBreakSummary?: string | null;
}

export interface CompetitionRegistrationEntry {
  entryPublicId: string;
  divisionPublicId: string;
  divisionName: string;
  entryType: CompetitionEntryType;
  status: string;
  reservePosition?: number | null;
  requestedAt: string;
  decisionReason?: string | null;
}

export interface CompetitionRegistrationStatus {
  competitionPublicId: string;
  entrantPublicId?: string | null;
  displayName?: string | null;
  entries: CompetitionRegistrationEntry[];
}

export interface CompetitionRegistrationRequest {
  divisionPublicId: string;
  entryType: CompetitionEntryType;
  partnerEntrantPublicId?: string | null;
  clientMutationId: string;
  consent: CompetitionConsent;
}

export interface CompetitionRegistrationResponse {
  competitionPublicId: string;
  divisionPublicId: string;
  entryPublicId: string;
  entryType: CompetitionEntryType;
  status: string;
  entrantPublicIds: string[];
  requestedAt: string;
  replayed: boolean;
}

export interface CompetitionPairInviteRequest {
  divisionPublicId: string;
  clientMutationId: string;
  inviteToken: string;
  consent: CompetitionConsent;
}

export interface CompetitionPairInviteResponse {
  invitationPublicId: string;
  competitionPublicId: string;
  divisionPublicId: string;
  competitionName: string;
  divisionName: string;
  inviterDisplayName: string;
  status: string;
  inviteToken?: string | null;
  expiresAt: string;
  entryPublicId?: string | null;
  replayed: boolean;
}

export interface CompetitionClaimResponse {
  competitionPublicId: string;
  entrantPublicId: string;
  claimStatus: string;
  divisionPublicIds: string[];
  replayed: boolean;
}

export type CompetitionOrganizerEntryAction = 'approve' | 'reserve' | 'decline' | 'withdraw';

export interface CompetitionOrganizerSummary {
  leagueType?: "DOUBLES_PILOT" | null;
  competitionPublicId: string;
  slug: string;
  name: string;
  sportCode: string;
  timezone: string;
  status: string;
  visibility: string;
  registrationClosesAt?: string | null;
  competitionStartsAt?: string | null;
  competitionEndsAt?: string | null;
  entrantsLockedAt?: string | null;
  drawPublishedAt?: string | null;
  divisionCount: number;
  pendingEntryCount: number;
  approvedEntryCount: number;
  reserveEntryCount: number;
}

export interface CompetitionOrganizerCollection {
  competitions: CompetitionOrganizerSummary[];
}

export interface CompetitionOrganizerEntrantMember {
  entrantPublicId: string;
  version: number;
  displayName: string;
  levelLabel?: string | null;
  homeArea?: string | null;
  linkedAccount: boolean;
  claimStatus?: string | null;
  sourceCode?: string | null;
}

export interface CompetitionOrganizerEntry {
  entryPublicId: string;
  version: number;
  divisionPublicId: string;
  divisionName: string;
  formatCode: string;
  entryType: CompetitionEntryType;
  status: string;
  reservePosition?: number | null;
  requestedAt: string;
  decisionReason?: string | null;
  members: CompetitionOrganizerEntrantMember[];
}

export interface CompetitionOrganizerEntrantQueue {
  competitionPublicId: string;
  competitionStatus: string;
  competitionVersion: number;
  entrantsLockedAt?: string | null;
  entries: CompetitionOrganizerEntry[];
}

export interface CompetitionOrganizerCommandRequest {
  clientMutationId: string;
  expectedVersion: number;
  reason?: string | null;
}

export interface CompetitionOrganizerReplacementRequest extends CompetitionOrganizerCommandRequest {
  outgoingEntrantPublicId: string;
  replacementEntrantPublicId: string;
  reason: string;
}

export interface CompetitionOrganizerMergeRequest {
  clientMutationId: string;
  sourceEntrantPublicId: string;
  expectedSourceVersion: number;
  canonicalEntrantPublicId: string;
  expectedCanonicalVersion: number;
  reason: string;
}

export interface CompetitionOrganizerMutationResponse {
  competitionPublicId: string;
  action: string;
  entityType: string;
  entityPublicId: string;
  status: string;
  version: number;
  reservePosition?: number | null;
  occurredAt: string;
  replayed: boolean;
}

export interface CompetitionEntrantLockResponse {
  competitionPublicId: string;
  status: string;
  version: number;
  entrantsLockedAt: string;
  divisions: Array<{
    divisionPublicId: string;
    name: string;
    status: string;
    approvedEntries: number;
    reserveEntries: number;
    lockedAt: string;
  }>;
  replayed: boolean;
}

export interface CompetitionDrawSide {
  entryPublicId?: string | null;
  displayNames: string[];
  mine: boolean;
}

export interface CompetitionDrawMatch {
  matchPublicId?: string | null;
  matchNumber: number;
  sideA: CompetitionDrawSide;
  sideB: CompetitionDrawSide;
}

export interface CompetitionDrawDivision {
  drawPublicId?: string | null;
  divisionPublicId: string;
  divisionName: string;
  formatCode: string;
  algorithmVersion: string;
  drawVersion: number;
  sourceEntryHash?: string | null;
  contentHash?: string | null;
  status: string;
  rounds: Array<{ roundNumber: number; matches: CompetitionDrawMatch[] }>;
  byes: Array<{ roundNumber: number; side: CompetitionDrawSide }>;
}

export interface CompetitionDrawResponse {
  competitionPublicId: string;
  competitionVersion: number;
  status: 'NOT_GENERATED' | 'NOT_PUBLISHED' | 'DRAFT' | 'PUBLISHED' | string;
  drawPublishedAt?: string | null;
  divisions: CompetitionDrawDivision[];
  replayed: boolean;
}

export interface CompetitionDrawCommandRequest {
  clientMutationId: string;
  expectedCompetitionVersion: number;
}

export type CompetitionMatchStatus =
  | 'NO_SCHEDULE' | 'PROPOSED' | 'SCHEDULED' | 'OVERDUE' | 'ACTION_REQUIRED';

export interface CompetitionScheduleSide {
  entryPublicId: string;
  displayNames: string[];
  mine: boolean;
  availability?: string | null;
}

export interface CompetitionScheduleProposal {
  proposalPublicId: string;
  proposerEntryPublicId: string;
  startsAt: string;
  timezone: string;
  courtName?: string | null;
  courtAddress?: string | null;
  source: 'PROPOSAL' | 'OFFLINE_RECORDED' | string;
  status: 'OPEN' | 'ACCEPTED' | 'SUPERSEDED' | string;
  mine: boolean;
  createdAt: string;
}

export interface CompetitionScheduleMatch {
  matchPublicId: string;
  divisionPublicId: string;
  divisionName: string;
  formatCode: string;
  roundNumber: number;
  matchNumber: number;
  deadlineAt?: string | null;
  status: CompetitionMatchStatus | string;
  version: number;
  mineSide?: 'A' | 'B' | null;
  sideA: CompetitionScheduleSide;
  sideB: CompetitionScheduleSide;
  schedule?: {
    revisionPublicId: string;
    revisionNumber: number;
    startsAt: string;
    timezone: string;
    courtName?: string | null;
    courtAddress?: string | null;
    source: string;
    agreedAt: string;
  } | null;
  proposals: CompetitionScheduleProposal[];
}

export interface CompetitionScheduleResponse {
  competitionPublicId: string;
  competitionVersion: number;
  timezone: string;
  organizer: boolean;
  matches: CompetitionScheduleMatch[];
  replayed: boolean;
}

export interface CompetitionResultSide {
  entryPublicId: string;
  displayNames: string[];
  mine: boolean;
}

export interface CompetitionResultVersion {
  resultVersionPublicId: string;
  versionNumber: number;
  outcomeCode: string;
  canonicalScore?: string | null;
  durationMinutes?: number | null;
  winnerSide: 'A' | 'B' | 'NONE' | string;
  sourceCode: string;
  submittedBySide?: 'A' | 'B' | null;
  submittedAt: string;
  responseCode?: string | null;
  disputeReason?: string | null;
  finalizedAt?: string | null;
}

export interface CompetitionResultMatch {
  matchPublicId: string;
  divisionPublicId: string;
  divisionName: string;
  formatCode: string;
  scoringFormatCode: string;
  roundNumber: number;
  matchNumber: number;
  version: number;
  mineSide?: 'A' | 'B' | null;
  sideA: CompetitionResultSide;
  sideB: CompetitionResultSide;
  resultState: 'NOT_REPORTED' | 'AWAITING_RESULT' | 'AWAITING_CONFIRMATION' | 'DISPUTED' | 'FINALIZED' | 'VOIDED' | string;
  currentResult?: CompetitionResultVersion | null;
  startsAt?: string | null;
}

export interface CompetitionStanding {
  rank: number;
  entryPublicId: string;
  displayNames: string[];
  played: number;
  wins: number;
  losses: number;
  points: number;
  setsWon: number;
  setsLost: number;
  gamesWon: number;
  gamesLost: number;
  tieDetail?: string | null;
  mine: boolean;
}

export interface CompetitionResultDivision {
  divisionPublicId: string;
  name: string;
  formatCode: string;
  scoringFormatCode: string;
  leaderboardState: 'PROVISIONAL' | 'FINAL' | string;
  completionBlockerCount: number;
  standings: CompetitionStanding[];
  winnerEntryPublicIds: string[];
}

export interface CompetitionResultsResponse {
  competitionPublicId: string;
  competitionName: string;
  organizer: boolean;
  tieRuleCode: string;
  divisions: CompetitionResultDivision[];
  matches: CompetitionResultMatch[];
  replayed: boolean;
}

export interface CompetitionLaunchReadinessIssue {
  code: string;
  message: string;
  divisionPublicId?: string | null;
}

export interface CompetitionLaunchReadinessDivision {
  divisionPublicId: string;
  name: string;
  formatCode: string;
  scoringFormatCode: string;
  capacity: number;
  approvedEntries: number;
  ready: boolean;
  issueCodes: string[];
}

export interface CompetitionLaunchReadiness {
  competitionPublicId: string;
  competitionName: string;
  timezone: string;
  competitionVersion: number;
  ready: boolean;
  divisions: CompetitionLaunchReadinessDivision[];
  issues: CompetitionLaunchReadinessIssue[];
}

export interface CompetitionExportDownload {
  blob: Blob;
  fileName: string;
}

export interface CompetitionResultResolutionRequest {
  clientMutationId: string;
  expectedMatchVersion: number;
  outcomeCode: 'COMPLETED' | 'RETIREMENT' | 'WALKOVER' | 'NO_SHOW' | 'ABANDONED' | 'VOID';
  scoreText?: string | null;
  durationMinutes?: number | null;
  winnerSide: 'A' | 'B' | 'NONE';
  reason: string;
}

export interface CompetitionScheduleAssignmentRequest {
  clientMutationId: string;
  expectedMatchVersion: number;
  startsAt: string;
  courtName?: string;
  courtAddress?: string;
  reason: string;
}

export interface CompetitionRoundDeadlineRequest {
  clientMutationId: string;
  expectedCompetitionVersion: number;
  divisionPublicId: string;
  roundNumber: number;
  deadlineAt: string;
  reason: string;
}

export async function getPublicCompetition(slug: string): Promise<PublicCompetition> {
  const response = await apiClient.get<PublicCompetition>(
    `/api/v1/public/competitions/${encodeURIComponent(slug)}`
  );
  return response.data;
}

export async function getPublicCompetitionDraw(slug: string): Promise<CompetitionDrawResponse> {
  const response = await apiClient.get<CompetitionDrawResponse>(
    `/api/v1/public/competitions/${encodeURIComponent(slug)}/draw`
  );
  return response.data;
}

export async function getPublicCompetitionDivisions(
  slug: string
): Promise<PublicCompetitionDivision[]> {
  const response = await apiClient.get<PublicCompetitionDivision[]>(
    `/api/v1/public/competitions/${encodeURIComponent(slug)}/divisions`
  );
  return response.data;
}

export async function getMyCompetitionRegistration(
  competitionPublicId: string
): Promise<CompetitionRegistrationStatus> {
  const response = await apiClient.get<CompetitionRegistrationStatus>(
    `/api/v1/web/competitions/${competitionPublicId}/registrations/me`
  );
  return response.data;
}

export async function registerForCompetition(
  competitionPublicId: string,
  request: CompetitionRegistrationRequest
): Promise<CompetitionRegistrationResponse> {
  const response = await apiClient.post<CompetitionRegistrationResponse>(
    `/api/v1/web/competitions/${competitionPublicId}/registrations`,
    request
  );
  return response.data;
}

export async function createCompetitionPairInvite(
  competitionPublicId: string,
  request: CompetitionPairInviteRequest
): Promise<CompetitionPairInviteResponse> {
  const response = await apiClient.post<CompetitionPairInviteResponse>(
    `/api/v1/web/competitions/${competitionPublicId}/pair-invitations`,
    request
  );
  return response.data;
}

export async function previewCompetitionPairInvite(
  inviteToken: string
): Promise<CompetitionPairInviteResponse> {
  const response = await apiClient.post<CompetitionPairInviteResponse>(
    '/api/v1/web/competition-pair-invitations/preview',
    { inviteToken }
  );
  return response.data;
}

export async function acceptCompetitionPairInvite(
  inviteToken: string,
  clientMutationId: string,
  consent: CompetitionConsent
): Promise<CompetitionPairInviteResponse> {
  const response = await apiClient.post<CompetitionPairInviteResponse>(
    '/api/v1/web/competition-pair-invitations/accept',
    { inviteToken, clientMutationId, consent }
  );
  return response.data;
}

export async function claimCompetitionRegistration(
  token: string,
  clientMutationId: string,
  consent: CompetitionConsent
): Promise<CompetitionClaimResponse> {
  const response = await apiClient.post<CompetitionClaimResponse>(
    '/api/v1/web/competition-claims',
    { token, clientMutationId, consent }
  );
  return response.data;
}
export async function getOrganizerCompetitions(): Promise<CompetitionOrganizerCollection> {
  const response = await apiClient.get<CompetitionOrganizerCollection>(
    '/api/v1/web/organizer/competitions'
  );
  return response.data;
}

export async function getOrganizerCompetitionEntrants(
  competitionPublicId: string,
  filters: { divisionPublicId?: string; status?: string } = {}
): Promise<CompetitionOrganizerEntrantQueue> {
  const response = await apiClient.get<CompetitionOrganizerEntrantQueue>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/entrants`,
    { params: filters }
  );
  return response.data;
}

export async function getOrganizerCompetitionDraw(
  competitionPublicId: string
): Promise<CompetitionDrawResponse> {
  const response = await apiClient.get<CompetitionDrawResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/draw`
  );
  return response.data;
}

export async function generateOrganizerCompetitionDraw(
  competitionPublicId: string,
  request: CompetitionDrawCommandRequest
): Promise<CompetitionDrawResponse> {
  const response = await apiClient.post<CompetitionDrawResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/draw/generate`,
    request
  );
  return response.data;
}

export async function publishOrganizerCompetitionDraw(
  competitionPublicId: string,
  request: CompetitionDrawCommandRequest
): Promise<CompetitionDrawResponse> {
  const response = await apiClient.post<CompetitionDrawResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/draw/publish`,
    request
  );
  return response.data;
}

export async function getOrganizerCompetitionSchedule(
  competitionPublicId: string
): Promise<CompetitionScheduleResponse> {
  const response = await apiClient.get<CompetitionScheduleResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/schedule`
  );
  return response.data;
}

export async function getOrganizerCompetitionResults(
  competitionPublicId: string
): Promise<CompetitionResultsResponse> {
  const response = await apiClient.get<CompetitionResultsResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/results`
  );
  return response.data;
}

export async function getOrganizerCompetitionLaunchReadiness(
  competitionPublicId: string
): Promise<CompetitionLaunchReadiness> {
  const response = await apiClient.get<CompetitionLaunchReadiness>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/launch-readiness`
  );
  return response.data;
}

export async function downloadOrganizerCompetitionExport(
  competitionPublicId: string
): Promise<CompetitionExportDownload> {
  const response = await apiClient.get<Blob>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/export.csv`,
    { responseType: 'blob' }
  );
  const disposition = response.headers['content-disposition'] as string | undefined;
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const quoted = disposition?.match(/filename="([^"]+)"/i)?.[1];
  return {
    blob: response.data,
    fileName: encoded ? decodeURIComponent(encoded) : quoted || 'tark-competition-export.csv',
  };
}

export async function resolveOrganizerCompetitionResult(
  competitionPublicId: string,
  matchPublicId: string,
  request: CompetitionResultResolutionRequest
): Promise<CompetitionResultsResponse> {
  const response = await apiClient.post<CompetitionResultsResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/matches/${matchPublicId}/results/resolve`,
    request
  );
  return response.data;
}

export async function completeOrganizerCompetitionDivision(
  competitionPublicId: string,
  divisionPublicId: string,
  request: { clientMutationId: string; reason: string }
): Promise<CompetitionResultsResponse> {
  const response = await apiClient.post<CompetitionResultsResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/divisions/${divisionPublicId}/complete`,
    request
  );
  return response.data;
}

export async function getPublicCompetitionResults(slug: string): Promise<CompetitionResultsResponse> {
  const response = await apiClient.get<CompetitionResultsResponse>(
    `/api/v1/public/competitions/${slug}/results`
  );
  return response.data;
}

export async function assignOrganizerCompetitionMatch(
  competitionPublicId: string,
  matchPublicId: string,
  request: CompetitionScheduleAssignmentRequest
): Promise<CompetitionScheduleResponse> {
  const response = await apiClient.post<CompetitionScheduleResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/matches/${matchPublicId}/assign`,
    request
  );
  return response.data;
}

export async function setOrganizerCompetitionRoundDeadline(
  competitionPublicId: string,
  request: CompetitionRoundDeadlineRequest
): Promise<CompetitionScheduleResponse> {
  const response = await apiClient.post<CompetitionScheduleResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/round-deadlines`,
    request
  );
  return response.data;
}

export async function nudgeOrganizerCompetitionMatch(
  competitionPublicId: string,
  matchPublicId: string,
  clientMutationId: string
): Promise<CompetitionScheduleResponse> {
  const response = await apiClient.post<CompetitionScheduleResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/matches/${matchPublicId}/nudge`,
    { clientMutationId }
  );
  return response.data;
}

export async function reviewOrganizerCompetitionEntry(
  competitionPublicId: string,
  entryPublicId: string,
  action: CompetitionOrganizerEntryAction,
  request: CompetitionOrganizerCommandRequest
): Promise<CompetitionOrganizerMutationResponse> {
  const response = await apiClient.post<CompetitionOrganizerMutationResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/entries/${entryPublicId}/${action}`,
    request
  );
  return response.data;
}

export async function replaceOrganizerCompetitionEntry(
  competitionPublicId: string,
  entryPublicId: string,
  request: CompetitionOrganizerReplacementRequest
): Promise<CompetitionOrganizerMutationResponse> {
  const response = await apiClient.post<CompetitionOrganizerMutationResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/entries/${entryPublicId}/replace`,
    request
  );
  return response.data;
}

export async function mergeOrganizerCompetitionEntrants(
  competitionPublicId: string,
  request: CompetitionOrganizerMergeRequest
): Promise<CompetitionOrganizerMutationResponse> {
  const response = await apiClient.post<CompetitionOrganizerMutationResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/entrant-duplicates/resolve`,
    request
  );
  return response.data;
}

export async function lockOrganizerCompetitionEntrants(
  competitionPublicId: string,
  request: CompetitionOrganizerCommandRequest
): Promise<CompetitionEntrantLockResponse> {
  const response = await apiClient.post<CompetitionEntrantLockResponse>(
    `/api/v1/web/organizer/competitions/${competitionPublicId}/entrants/lock`,
    request
  );
  return response.data;
}
